import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { ArrowLeft, TrendingUp, Building2, Fuel, Info, AlertTriangle, Shield, MapPin, HelpCircle, Save, Check, DollarSign, Users, Calculator, FileText, Download } from 'lucide-react';
import { BrokerConnectionModal } from './valuation/BrokerConnectionModal';
import { Button } from './ui/Button';
import { useSupabase } from '../hooks/useSupabase';
import { useAuthStore } from '../store/useAuthStore';
import { saveValuationData } from '../services/valuationService';
import { calculateEntrepreneurialMultiplier, defaultSynergyAnswers } from '../types/entrepreneurial-synergy';
import { ValuationCompetitorAnalysis } from './valuation/ValuationCompetitorAnalysis';
import TaxDepreciationDemo from './demo/TaxDepreciationDemo';
import LoanCalculatorDemo from './demo/LoanCalculatorDemo';
import { generateValuationPDF } from '../services/pdf/generatePDF';
import { toast } from 'react-toastify';
import { useOpComps } from '../hooks/useOpComps';
import { DualValuationDisplay, calculateDualValuation, ForwardMarginProjection, type DualValuationResult } from './valuation/DualValuationDisplay';
import { calculateFuelSupplyBenefit, type FuelSupplyBenefit } from '../utils/fuelSupplyBenefit';
import {
  computeLeasedFeeValue,
  computeRemainingTermYears,
  extractZipFromLocation,
  type LeasedFeeResult,
} from '../utils/leasedFeeValuation';

interface ValuationResultProps {
  data: any;
  predictedValue: number | null;
  onBack: () => void;
  apiResponse?: {
    result?: {
      predicted_business_value?: number;
      predicted_land_value?: number;
      total_station_value?: number;
      confidence_score?: number;
      gross_profit?: number;
      gross_revenue?: number;
      total_gallons?: number;
      depreciated_assets?: {
        Car_Wash?: { Depreciated_Cost: number; Annual_Depreciation: number };
        Car_Wash_Equipment?: { Depreciated_Cost: number; Annual_Depreciation: number };
        Convenience_Store?: { Depreciated_Cost: number; Annual_Depreciation: number };
        FFE?: { Depreciated_Cost: number; Annual_Depreciation: number };
        Food_Booth?: { Depreciated_Cost: number; Annual_Depreciation: number };
        Fueling_Canopy?: { Depreciated_Cost: number; Annual_Depreciation: number };
        Fueling_Equipment?: { Depreciated_Cost: number; Annual_Depreciation: number };
        Kiosk?: { Depreciated_Cost: number; Annual_Depreciation: number };
        Service_Station?: { Depreciated_Cost: number; Annual_Depreciation: number };
        Site_Improvements?: { Depreciated_Cost: number; Annual_Depreciation: number };
        UST?: { Depreciated_Cost: number; Annual_Depreciation: number };
      };
      tam_estimation?: {
        subject?: {
          annual_fuel_gallons?: number;
          annual_cstore_sales?: number;
          cstore_margin?: number;
        };
        estimates?: {
          projections?: {
            year_1?: {
              net_operating_income?: number;
            };
          };
          year_1_noi?: number;
        };
        diagnostics?: {
          comparables_analysis?: {
            comp_count?: number;
            avg_fuel_gallons?: number;
            avg_cstore_sales?: number;
            geography_description?: string;
          };
          benchmark?: {
            Annual_Fuel_Gallons_National?: number;
            Annual_Cstore_Sales_National?: number;
            Annual_Cstore_Margin_National?: number;
            Annual_Net_Operating_Income_National?: number;
          };
          forward_margin_projection?: ForwardMarginProjection;
        };
      };
    };
    market_insights?: {
      risk_assessment?: number;
      road_accessibility?: {
        accessibility_score?: number;
        analysis?: {
          area_type?: string;
          major_roads?: string[];
          points_of_interest?: string[];
          traffic_patterns?: string;
          county?: string;
        };
      };
      competitor_summary?: {
        total: number;
        independent: number;
        major_brands: number;
      };
      competitors?: Array<{
        name: string;
        address: string;
        lat: number;
        lng: number;
        is_major_brand: boolean;
      }>;
      market_trends?: {
        fuel_margin?: {
          current: number;
          year_ago: number;
          growth_rate: number;
        };
        store_revenue?: {
          current: number;
          year_ago: number;
          growth_rate: number;
        };
      };
    };
    validation?: {
      confidence_score?: number;
      is_valid?: boolean;
      issues?: string[];
      risk_level?: number;
    };
  };
}

interface AssetComponent {
  name: string;
  value: number;
  color: string;
  confidenceScore: number;
  hasTooltip?: boolean;
  tooltipText?: string;
}

interface ValuationBreakdown {
  landValue: number;
  buildingSiteValue: number;
  fixturesValue: number;
  ffeValue: number;
  businessValue: number;
  totalValue: number;
  detailedComponents: AssetComponent[];
}

export default function ValuationResult({ data, predictedValue, onBack, apiResponse }: ValuationResultProps) {
  // DEBUG: Log API response structure
  console.log('[ValuationResult] Component rendered');
  console.log('[ValuationResult] apiResponse:', !!apiResponse);
  console.log('[ValuationResult] apiResponse.result:', !!apiResponse?.result);
  console.log('[ValuationResult] apiResponse.result.tam_estimation:', !!apiResponse?.result?.tam_estimation);
  if (apiResponse?.result) {
    console.log('[ValuationResult] apiResponse.result keys:', Object.keys(apiResponse.result));
    console.log('[ValuationResult] Full apiResponse.result:', apiResponse.result);
  }
  if (apiResponse?.result?.tam_estimation) {
    console.log('[ValuationResult] TAM estimation keys:', Object.keys(apiResponse.result.tam_estimation));
    console.log('[ValuationResult] Full tam_estimation:', apiResponse.result.tam_estimation);
  }

  const navigate = useNavigate();
  const [brokerModalOpen, setBrokerModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeBreakdownTab, setActiveBreakdownTab] = useState<'detailed' | 'input'>('detailed');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Get Supabase client and authentication state
  const { supabase } = useSupabase();
  const { user, isAuthenticated } = useAuthStore();

  // Fetch OpComps data for comparative analysis
  const { opComps, loading: opCompsLoading, error: opCompsError } = useOpComps();
  const [comparativeData, setComparativeData] = useState<any>(null);

  // Calculate comparative benchmarks when OpComps data is loaded
  useEffect(() => {
    if (opComps && opComps.length > 0 && data && apiResponse?.result) {
      console.log('[ValuationResult] Calculating comparative benchmarks from', opComps.length, 'OpComps');

      // Calculate cohort statistics
      const calculateCohortStats = (field: string) => {
        const values = opComps
          .map(comp => comp[field as keyof typeof comp])
          .filter((val): val is number => typeof val === 'number' && !isNaN(val));

        if (values.length === 0) return null;

        const sorted = [...values].sort((a, b) => a - b);
        const median = sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)];

        return {
          min: Math.min(...values),
          max: Math.max(...values),
          median,
          average: values.reduce((sum, val) => sum + val, 0) / values.length,
          p25: sorted[Math.floor(values.length * 0.25)],
          p75: sorted[Math.floor(values.length * 0.75)]
        };
      };

      // Get user's values from TAM and API response
      const userFuelVolume = apiResponse.result.total_gallons ||
                            (data.monthly_fuel_sales ? data.monthly_fuel_sales * 12 / 3.5 : 0); // Estimate from monthly sales
      const userCStoreSales = data.monthly_cstore_sales ? data.monthly_cstore_sales * 12 : 0;
      const userSiteSize = data.lot_size || data.site_size || 0;
      const userStoreBldgSize = data.building_size || data.store_size || 0;

      setComparativeData({
        fuelVolume: {
          userValue: userFuelVolume,
          ...calculateCohortStats('fuelVol')
        },
        cstoreSales: {
          userValue: userCStoreSales,
          ...calculateCohortStats('cStoreSales')
        },
        siteSize: {
          userValue: userSiteSize,
          ...calculateCohortStats('siteSize')
        },
        storeBldgSize: {
          userValue: userStoreBldgSize,
          ...calculateCohortStats('storeBldgSize')
        },
        cohortCount: opComps.length
      });
    }
  }, [opComps, data, apiResponse]);

  /**
   * Handles PDF generation and download
   */
  async function handleDownloadPDF() {
    try {
      setIsGeneratingPDF(true);
      toast.info('Generating your valuation report...');

      // Get the current valuation ID from the most recent save
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: latestVal, error: valError } = await supabase
        .from('valuations')
        .select('id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (valError || !latestVal) throw new Error('No saved valuation found. Please save your valuation first.');

      // Call our Edge Function
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-avm-report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ valuation_id: latestVal.id }),
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Report generation failed');
      }

      const { url } = await response.json();

      // Open PDF in new tab
      window.open(url, '_blank');
      toast.success('Report ready!');

    } catch (error: any) {
      console.error('PDF generation error:', error);
      toast.error(error.message || 'Failed to generate PDF report');
    } finally {
      setIsGeneratingPDF(false);
    }
  }
  
  /**
   * Handles saving the valuation data to Supabase
   */
  async function handleSaveValuation() {
    if (!isAuthenticated || !user) {
      setSaveStatus('error');
      setSaveError('Please log in to save this valuation');
      return;
    }
    
    try {
      setIsSaving(true);
      setSaveStatus('idle');
      setSaveError(null);
      
      // For Supabase, we need to use a proper UUID format for property_id
      // Generate a UUID for the property ID to match the database schema
      const propertyId = uuidv4();
      
      // For debugging and reference, we'll store some property identifiers in the data
      // This helps track where the valuation came from
      let referenceId = null;
      
      // Check for existing reference IDs
      if (data?.OBJECTID) referenceId = `OBJECTID-${data.OBJECTID}`;
      else if (data?.objectId) referenceId = `objectId-${data.objectId}`;
      else if (data?.object_id) referenceId = `object_id-${data.object_id}`;
      else if (data?.id) referenceId = `id-${data.id}`;
      
      // Log the IDs we're using
      console.log('Using property ID:', propertyId);
      console.log('Reference ID:', referenceId);
      
      // We always have a valid UUID now, so we don't need this check anymore
      
      // Generate a descriptive name for the valuation
      const valuationName = `Valuation for ${data.location || 'Property'} - ${new Date().toLocaleDateString()}`;
      
      // Ensure the data includes the API response and reference IDs for detailed breakdown
      const valuationData = {
        ...data,
        apiResponse,
        referenceId // Store the reference ID for traceability
      };
      
      // Call the service function to save the valuation
      const result = await saveValuationData(supabase, {
        userId: user.id,
        propertyId,
        name: valuationName,
        data: valuationData
      });
      
      if (result.success) {
        setSaveStatus('success');
      } else {
        setSaveStatus('error');
        setSaveError(result.error || 'Failed to save valuation');
      }
    } catch (error) {
      setSaveStatus('error');
      setSaveError((error as Error).message || 'An unexpected error occurred');
    } finally {
      setIsSaving(false);
      
      // Reset success status after 3 seconds
      // After success, reset the status to idle after 3 seconds
      if (saveStatus === 'success') {
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    }
  }
  
  // Auto-save valuation when results are displayed (with duplicate prevention)
  const hasAutoSaved = React.useRef(false);
  useEffect(() => {
    if (isAuthenticated && user && !hasAutoSaved.current && saveStatus === 'idle') {
      // Create a fingerprint from key valuation inputs to prevent duplicate saves across remounts
      const fingerprint = `${data.location}_${data.totalGrossProfit}_${data.annualGallons}_${predictedValue}`;
      const savedKey = `autosaved_${fingerprint}`;
      if (sessionStorage.getItem(savedKey)) {
        hasAutoSaved.current = true;
        return;
      }
      hasAutoSaved.current = true;
      sessionStorage.setItem(savedKey, Date.now().toString());
      handleSaveValuation();
    }
  }, [isAuthenticated, user]);

  // Format currency to millions with 2 decimal places
  const toMillions = (num: number) => {
    return (num / 1000000).toFixed(2);
  };
  
  // Format currency to K for thousands
  const toThousands = (num: number) => {
    return (num / 1000).toFixed(0);
  };

  // Format currency with commas, rounded to nearest $10,000 without decimal places
  const formatCurrency = (num: number) => {
    // Round to nearest $10,000
    const roundedValue = Math.round(num / 10000) * 10000;
    return roundedValue.toLocaleString('en-US');
  };
  
  // Format percentage
  const formatPercent = (num: number) => {
    return `${(num * 100).toFixed(1)}%`;
  };
  
  // Generate a valuation breakdown based on API response data or calculate from the total value
  const calculateValuationBreakdown = (totalValue: number): ValuationBreakdown => {
    // If we have API response data with detailed breakdown, use that
    if (apiResponse?.result) {
      const result = apiResponse.result;
      const landValue = result.predicted_land_value || 0;
      const totalBusinessValue = result.predicted_business_value || 0;
      const totalStationValue = result.total_station_value || 0;
      const assets = result.depreciated_assets || {};
      
      // Initialize with individual asset components
      const detailedComponents: AssetComponent[] = [];
      let totalDepreciatedAssets = 0;
      
      // 1. Land Value - directly from API
      if (landValue > 0) {
        detailedComponents.push({
          name: 'Land Value',
          value: landValue,
          color: 'bg-blue-700',
          confidenceScore: 7.8,
          hasTooltip: true,
          tooltipText: 'The underlying real estate (land only) without improvements or structures. Represents the raw land value as if vacant and ready for development.'
        });
      }
      
      // 2. Individual building components
      if (assets.Convenience_Store?.Depreciated_Cost) {
        const value = assets.Convenience_Store.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Convenience Store',
          value: value,
          color: 'bg-blue-600',
          confidenceScore: 8.3,
          hasTooltip: true,
          tooltipText: 'The main convenience store building including all structural elements, utilities, and permanent fixtures.'
        });
      }
      
      if (assets.Service_Station?.Depreciated_Cost) {
        const value = assets.Service_Station.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Service Station',
          value: value,
          color: 'bg-blue-600',
          confidenceScore: 8.2,
          hasTooltip: true,
          tooltipText: 'The service station building including service bays, lifts, and other integrated components for vehicle servicing.'
        });
      }
      
      if (assets.Kiosk?.Depreciated_Cost) {
        const value = assets.Kiosk.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Kiosk',
          value: value,
          color: 'bg-blue-600',
          confidenceScore: 8.1,
          hasTooltip: true,
          tooltipText: 'Small manned payment point building (50-250 sq ft) often used for fuel-only operations.'
        });
      }
      
      if (assets.Food_Booth?.Depreciated_Cost) {
        const value = assets.Food_Booth.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Food Booth',
          value: value,
          color: 'bg-blue-600',
          confidenceScore: 8.0,
          hasTooltip: true,
          tooltipText: 'Older convenience store style building (500-1,500 sq ft) often repurposed for food service operations.'
        });
      }
      
      if (assets.Car_Wash?.Depreciated_Cost) {
        const value = assets.Car_Wash.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Car Wash Structure',
          value: value,
          color: 'bg-blue-600',
          confidenceScore: 8.2,
          hasTooltip: true,
          tooltipText: 'The car wash building structure including tunnel, bays, and related permanent fixtures.'
        });
      }
      
      if (assets.Site_Improvements?.Depreciated_Cost) {
        const value = assets.Site_Improvements.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Site Improvements',
          value: value,
          color: 'bg-blue-600',
          confidenceScore: 8.0,
          hasTooltip: true,
          tooltipText: 'The improved site elements including paving, lighting, utility lines, signage, and landscaping.'
        });
      }
      
      // 3. Individual equipment components
      if (assets.Fueling_Equipment?.Depreciated_Cost) {
        const value = assets.Fueling_Equipment.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Fueling Equipment',
          value: value,
          color: 'bg-blue-500',
          confidenceScore: 8.8,
          hasTooltip: true,
          tooltipText: 'Fuel dispensers, pumps, lines, and monitoring systems.'
        });
      }
      
      if (assets.Fueling_Canopy?.Depreciated_Cost) {
        const value = assets.Fueling_Canopy.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Fueling Canopy',
          value: value,
          color: 'bg-blue-500',
          confidenceScore: 8.5,
          hasTooltip: true,
          tooltipText: 'The overhead structure covering the fuel dispensers, including lighting and signage fixtures.'
        });
      }
      
      if (assets.UST?.Depreciated_Cost) {
        const value = assets.UST.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Underground Storage Tanks',
          value: value,
          color: 'bg-blue-500',
          confidenceScore: 8.7,
          hasTooltip: true,
          tooltipText: 'Underground storage tanks and associated piping and monitoring systems.'
        });
      }
      
      if (assets.Car_Wash_Equipment?.Depreciated_Cost) {
        const value = assets.Car_Wash_Equipment.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Car Wash Equipment',
          value: value,
          color: 'bg-blue-500',
          confidenceScore: 8.6,
          hasTooltip: true,
          tooltipText: 'Car wash equipment including wash arms, arches, dryers, and supporting electrical systems.'
        });
      }
      
      // 4. Personal Property (FF&E)
      if (assets.FFE?.Depreciated_Cost) {
        const value = assets.FFE.Depreciated_Cost;
        totalDepreciatedAssets += value;
        detailedComponents.push({
          name: 'Furniture, Fixtures & Equipment',
          value: value,
          color: 'bg-blue-400',
          confidenceScore: 8.5,
          hasTooltip: true,
          tooltipText: 'Movable items such as shelving, coolers, food service equipment, point of sale systems, security equipment, and office furniture.'
        });
      }

      // 5. Calculate residual business value OR functional/external obsolescence
      // Formula: Total Station Value - Land Value - All Depreciated Assets = Raw Residual
      // Then combine with fuel supply benefit to get adjusted residual
      // If adjusted > 0: Residual Business Value (intangibles)
      // If adjusted < 0: Functional and/or External Obsolescence
      const rawResidualValue = totalStationValue - landValue - totalDepreciatedAssets;
      const fuelSupplyAdj = fuelSupplyBenefit.qualifies ? fuelSupplyBenefit.valuationAdjustment : 0;
      const adjustedResidual = rawResidualValue + fuelSupplyAdj;

      if (adjustedResidual > 0) {
        // Positive adjusted residual = Business/Intangible Value
        detailedComponents.push({
          name: 'Residual Business Value',
          value: adjustedResidual,
          color: 'bg-blue-300',
          confidenceScore: 7.9,
          hasTooltip: true,
          tooltipText: 'The intangible value of the business beyond its physical assets, including goodwill, branding, licenses, permits, customer relationships, and operational systems.' +
            (fuelSupplyAdj > 0 ? ` Includes a $${formatCurrency(fuelSupplyAdj)} fuel supply flexibility premium.` : '')
        });
      } else if (adjustedResidual < 0) {
        // Negative adjusted residual = Functional and/or External Obsolescence
        detailedComponents.push({
          name: 'Functional and/or External Obsolescence',
          value: adjustedResidual,
          color: 'bg-red-400',
          confidenceScore: 7.5,
          hasTooltip: true,
          tooltipText: 'The physical assets exceed the going-concern value of the business, indicating functional obsolescence (poor layout, inefficient design) and/or external obsolescence (market conditions, location issues). This is a negative adjustment reflecting that the property is worth less as an operating business than the sum of its depreciated components.' +
            (fuelSupplyAdj > 0 ? ` Net of a $${formatCurrency(fuelSupplyAdj)} fuel supply flexibility premium.` : '')
        });
      }

      // Use adjusted residual for validation (includes fuel supply benefit)
      const residualValue = adjustedResidual;

      // 6. Validate that all components sum to total value (including fuel supply)
      const adjustedTotal = totalStationValue + fuelSupplyAdj;
      const sumOfComponents = landValue + totalDepreciatedAssets + residualValue;
      const difference = Math.abs(adjustedTotal - sumOfComponents);
      const roundingTolerance = 20000;
      const strictTolerance = 100;

      if (difference > roundingTolerance) {
        console.warn('⚠️ Valuation Component Validation Error:', {
          adjustedTotal,
          landValue,
          totalDepreciatedAssets,
          residualValue,
          fuelSupplyAdj,
          sumOfComponents,
          difference: adjustedTotal - sumOfComponents,
        });

        detailedComponents.push({
          name: '⚠️ Calculation Discrepancy',
          value: adjustedTotal - sumOfComponents,
          color: 'bg-yellow-500',
          confidenceScore: 0,
          hasTooltip: true,
          tooltipText: `There is a $${Math.abs(adjustedTotal - sumOfComponents).toLocaleString()} mathematical discrepancy between the total and sum of components.`
        });
      } else if (difference > strictTolerance) {
        console.log('ℹ️ Minor Rounding Difference:', { adjustedTotal, sumOfComponents, difference });

        detailedComponents.push({
          name: 'Rounding Adjustment',
          value: adjustedTotal - sumOfComponents,
          color: 'bg-gray-400',
          confidenceScore: 9.5,
          hasTooltip: true,
          tooltipText: `Minor rounding adjustment of $${Math.abs(adjustedTotal - sumOfComponents).toLocaleString()}.`
        });
      } else {
        console.log('✅ Valuation Component Validation Passed:', {
          adjustedTotal,
          landValue,
          totalDepreciatedAssets,
          residualValue,
          fuelSupplyAdj,
          sumOfComponents,
          difference: adjustedTotal - sumOfComponents
        });
      }

      // Calculate consolidated category values for the return object
      // 1. Land value is already extracted
      // 2. Building & site value - combine all building assets
      const buildingSiteValue = (
        (assets.Convenience_Store?.Depreciated_Cost || 0) +
        (assets.Kiosk?.Depreciated_Cost || 0) +
        (assets.Service_Station?.Depreciated_Cost || 0) +
        (assets.Site_Improvements?.Depreciated_Cost || 0) +
        (assets.Food_Booth?.Depreciated_Cost || 0) +
        (assets.Car_Wash?.Depreciated_Cost || 0)
      );

      // 3. Fixtures (permanent equipment)
      const fixturesValue = (
        (assets.Fueling_Equipment?.Depreciated_Cost || 0) +
        (assets.Fueling_Canopy?.Depreciated_Cost || 0) +
        (assets.UST?.Depreciated_Cost || 0) +
        (assets.Car_Wash_Equipment?.Depreciated_Cost || 0)
      );

      // 4. FFE value
      const ffeValue = assets.FFE?.Depreciated_Cost || 0;

      // 5. Business value (using the residual calculation - can be positive or negative)
      const businessValue = residualValue;
      
      return {
        landValue,
        buildingSiteValue,
        fixturesValue,
        ffeValue,
        businessValue,
        totalValue: totalStationValue + fuelSupplyAdj,
        detailedComponents
      };
    } 
    
    // Fallback calculations if we don't have the API response with detailed breakdown
    if (!totalValue) return {
      landValue: 0,
      buildingSiteValue: 0,
      fixturesValue: 0,
      ffeValue: 0,
      businessValue: 0,
      totalValue: 0,
      detailedComponents: []
    };
    
    // Approximate percentages based on industry standards
    const landPercent = 0.35;
    const buildingPercent = 0.20;
    const fixturesPercent = 0.15;
    const ffePercent = 0.10;
    const businessPercent = 0.20;
    
    const landValue = totalValue * landPercent;
    const buildingSiteValue = totalValue * buildingPercent;
    const fixturesValue = totalValue * fixturesPercent;
    const ffeValue = totalValue * ffePercent;
    const businessValue = totalValue * businessPercent;
    
    // Create simplified component breakdown for fallback
    const detailedComponents: AssetComponent[] = [
      {
        name: 'Land Value',
        value: landValue,
        color: 'bg-blue-700',
        confidenceScore: 7.8
      },
      {
        name: 'Building & Site Improvements',
        value: buildingSiteValue,
        color: 'bg-blue-600',
        confidenceScore: 8.2
      },
      {
        name: 'Permanent Fixtures (Fueling Equipment)',
        value: fixturesValue,
        color: 'bg-blue-500',
        confidenceScore: 9.0
      },
      {
        name: 'Furniture, Fixtures & Equipment',
        value: ffeValue,
        color: 'bg-blue-400',
        confidenceScore: 8.5
      },
      {
        name: 'Business Value',
        value: businessValue,
        color: 'bg-blue-300',
        confidenceScore: 7.9
      }
    ];
    
    return {
      landValue,
      buildingSiteValue,
      fixturesValue,
      ffeValue,
      businessValue,
      totalValue,
      detailedComponents
    };
  };
  
  // Extract market insights
  const marketInsights = apiResponse?.market_insights || {};
  const validation = apiResponse?.validation || {};
  const competitors = marketInsights.competitor_summary || { total: 0, independent: 0, major_brands: 0 };
  const accessibility = marketInsights.road_accessibility?.accessibility_score || 0;
  const riskAssessment = marketInsights.risk_assessment || 0;
  const marketRiskLevel = riskAssessment || 0;
  const validationRiskLevel = validation.risk_level || 0;
  const marketTrends = marketInsights.market_trends || {};
  
  // Calculate overall risk level (1-10 scale, lower is better)
  const overallRiskLevel = Math.round((marketRiskLevel + validationRiskLevel) / 2);
  
  // Get confidence score
  const confidenceScore = apiResponse?.result?.confidence_score || validation.confidence_score || 0;
  
  // Format risk level as text
  const getRiskLevelText = (level: number) => {
    if (level <= 3) return 'Low';
    if (level <= 6) return 'Medium';
    return 'High';
  };
  
  // Use the API response data when available, or fall back to the predicted value
  const baseValue = apiResponse?.result?.total_station_value || predictedValue || 0;

  // Calculate fuel supply benefit adjustment
  const grossProfit = apiResponse?.result?.gross_profit || 0;
  // Check if operating expenses are already annual
  const annualOpEx = data.isAnnual
    ? (data.operatingExpenses || 0)
    : (data.operatingExpenses || 0) * 12;
  const noi = grossProfit - annualOpEx;

  const fuelSupplyBenefit = calculateFuelSupplyBenefit(
    baseValue,
    grossProfit,
    noi,
    data.brandAffiliation || 'unbranded',
    data.fuelSupplyYears || 0
  );

  // Use adjusted value if fuel supply benefit applies
  const totalValue = fuelSupplyBenefit.qualifies ? fuelSupplyBenefit.adjustedValue : baseValue;

  // Leased fee asset valuation. Computed entirely client-side off form data.
  // Capitalized as a separate real-estate income stream so the going-concern
  // multiple isn't applied to rent. Only runs when there's a leased QSR with
  // a monthly rent on file.
  const leasedFee: LeasedFeeResult | null =
    data.hasLeasedQSR && (data.qsrCurrentMonthlyRent || 0) > 0
      ? computeLeasedFeeValue({
          monthlyRent: data.qsrCurrentMonthlyRent || 0,
          tenantName: data.qsrTenantName,
          guarantyType: data.qsrGuarantyType,
          remainingTermYears: computeRemainingTermYears({
            qsrIsMonthToMonth: data.qsrIsMonthToMonth,
            qsrLeaseEndYear: data.qsrLeaseEndYear,
          }),
          zipCode: extractZipFromLocation(data.location),
        })
      : null;
  const leasedFeeValue = leasedFee?.leasedFeeValue || 0;
  const grandTotalValue = totalValue + leasedFeeValue;

  // IMPORTANT: Update predictedValue to reflect the fuel supply adjustment
  // This ensures all three value boxes use the same adjusted base
  const displayValue = grandTotalValue;
  const valuationBreakdown = calculateValuationBreakdown(totalValue);

  // Splice the leased fee into the breakdown as its own line item so the
  // primary results table shows it between Residual Business Value and
  // the total. It's already excluded from the regressor's going-concern
  // calculation, so adding it here does not double-count.
  if (leasedFee && leasedFee.leasedFeeValue > 0) {
    valuationBreakdown.detailedComponents.push({
      name: 'Leased Fee Asset (QSR)',
      value: leasedFee.leasedFeeValue,
      color: 'bg-amber-600',
      confidenceScore: 8.0,
      hasTooltip: true,
      tooltipText:
        `Capitalized value of the leased QSR rent stream. ` +
        `Annual NOI of $${Math.round(leasedFee.noi).toLocaleString()} ` +
        `divided by an applied cap rate of ${(leasedFee.capRate.appliedRate * 100).toFixed(2)}% ` +
        `(Tier ${leasedFee.capRate.tenantTier}${leasedFee.capRate.hcol ? ', HCOL zip' : ''}). ` +
        `Valued separately from the going concern via income capitalization. ` +
        `See methodology breakdown on the Input tab.`,
    });
  }

  // Bucket each line item into "real property" (illiquid, tied to location)
  // vs "personal property & intangibles" (transferable with operator).
  // Underwriters use this split to gauge what's collateralizable.
  const PERSONAL_PROPERTY_NAMES = new Set([
    'Furniture, Fixtures & Equipment',
    'Residual Business Value',
    'Functional and/or External Obsolescence',
  ]);
  const realPropertyItems = valuationBreakdown.detailedComponents.filter(
    (c) => !PERSONAL_PROPERTY_NAMES.has(c.name)
  );
  const personalPropertyItems = valuationBreakdown.detailedComponents.filter(
    (c) => PERSONAL_PROPERTY_NAMES.has(c.name)
  );
  const realPropertyTotal = realPropertyItems.reduce((sum, c) => sum + c.value, 0);
  const personalPropertyTotal = personalPropertyItems.reduce((sum, c) => sum + c.value, 0);
  const allocationGrandTotal = realPropertyTotal + personalPropertyTotal;
  const realPropertyPct =
    allocationGrandTotal > 0 ? (realPropertyTotal / allocationGrandTotal) * 100 : 0;
  const personalPropertyPct =
    allocationGrandTotal > 0 ? (personalPropertyTotal / allocationGrandTotal) * 100 : 0;

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">

      {/* Valuation Disclaimer */}
      <div className="mb-6 bg-slate-50 border border-slate-200 rounded-lg p-4">
        <p className="text-xs text-slate-600 leading-relaxed">
          <span className="font-semibold">Disclaimer:</span> This valuation is an estimate based on available market data, comparable sales, and statistical modeling. It is not a certified appraisal and should not be used as the sole basis for financial, legal, or investment decisions. We recommend consulting a licensed appraiser or broker for formal property valuations.
        </p>
      </div>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-6">Property Valuation Summary</h2>

        {/* Three-column valuation display */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">

          {/* Cost to Compete - Left Column */}
          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 flex flex-col">
            <h3 className="text-lg font-semibold text-orange-800 mb-2 h-14 flex items-center">Cost to Compete</h3>
            <p className="text-3xl font-bold text-orange-600 mb-2">
              ${(() => {
                // Cost table: asset effective life and condition → years depreciated
                const COST_TABLE: Record<string, { effectiveLife: number; depYears: Record<string, number> }> = {
                  Car_Wash:          { effectiveLife: 40, depYears: { New: 1, Excellent: 1, Good: 5,  Average: 10, Fair: 15 } },
                  Car_Wash_Equipment:{ effectiveLife: 15, depYears: { New: 1, Excellent: 1, Good: 5,  Average: 7,  Fair: 10 } },
                  Convenience_Store: { effectiveLife: 40, depYears: { New: 1, Excellent: 1, Good: 6,  Average: 10, Fair: 15 } },
                  FFE:               { effectiveLife: 15, depYears: { New: 1, Excellent: 1, Good: 3,  Average: 6,  Fair: 10 } },
                  Food_Booth:        { effectiveLife: 40, depYears: { New: 1, Excellent: 1, Good: 5,  Average: 10, Fair: 15 } },
                  Fueling_Canopy:    { effectiveLife: 25, depYears: { New: 1, Excellent: 1, Good: 4,  Average: 7,  Fair: 8  } },
                  Fueling_Equipment: { effectiveLife: 15, depYears: { New: 1, Excellent: 1, Good: 3,  Average: 6,  Fair: 9  } },
                  Kiosk:             { effectiveLife: 40, depYears: { New: 1, Excellent: 1, Good: 6,  Average: 10, Fair: 15 } },
                  Service_Station:   { effectiveLife: 40, depYears: { New: 1, Excellent: 1, Good: 7,  Average: 10, Fair: 15 } },
                  Site_Improvements: { effectiveLife: 21, depYears: { New: 1, Excellent: 1, Good: 3,  Average: 5,  Fair: 8  } },
                  UST:               { effectiveLife: 15, depYears: { New: 1, Excellent: 1, Good: 4,  Average: 7,  Fair: 12 } },
                };

                const assets = (apiResponse?.result?.depreciated_assets || {}) as Record<string, { Depreciated_Cost: number; Annual_Depreciation: number } | undefined>;
                const userAssets = (data.assets || {}) as Record<string, { condition: string; size: number } | undefined>;
                let totalRCN = 0;

                // Reverse depreciation for each asset using cost table
                for (const key of Object.keys(assets)) {
                  const depreciatedCost = assets[key]?.Depreciated_Cost || 0;
                  if (depreciatedCost <= 0) continue;

                  const costEntry = COST_TABLE[key];
                  if (!costEntry) { totalRCN += depreciatedCost; continue; }

                  const userCondition = userAssets[key]?.condition || 'Average';
                  const yearsDep = costEntry.depYears[userCondition] ?? costEntry.depYears['Average'];
                  const retainedPct = 1 - (yearsDep / costEntry.effectiveLife);

                  totalRCN += retainedPct > 0 ? depreciatedCost / retainedPct : depreciatedCost;
                }

                // Add land value (land doesn't depreciate)
                const landValue = apiResponse?.result?.predicted_land_value || 0;

                // Cost to Compete = (RCN + Land + 25% indirect costs) less 20% entrepreneurial incentive
                const grossRCN = totalRCN + landValue;
                const withIndirect = grossRCN * 1.25;
                const costToCompete = withIndirect * 0.80;

                console.log('[Cost to Compete]', {
                  totalRCN: totalRCN.toFixed(0),
                  landValue: landValue.toFixed(0),
                  grossRCN: grossRCN.toFixed(0),
                  withIndirect: withIndirect.toFixed(0),
                  costToCompete: costToCompete.toFixed(0)
                });

                return costToCompete >= 1000000
                  ? `${(costToCompete / 1000000).toFixed(2)}M`
                  : `${(costToCompete / 1000).toFixed(0)}K`;
              })()}
            </p>
            <p className="text-sm text-orange-700">
              RCN + 25% indirect costs, less 20% entrepreneurial incentive
            </p>
          </div>

          {/* Estimated Property Value - Center Column */}
          <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-200 flex flex-col">
            <h3 className="text-lg font-semibold text-blue-800 mb-2 h-14 flex items-center">Estimated Property Value</h3>
            <p className="text-3xl font-bold text-blue-600 mb-2">
              ${displayValue ? toMillions(displayValue) : '0.00'}M
            </p>
            <p className="text-sm text-blue-700">
              Current market value assessment
            </p>
          </div>

          {/* Entrepreneurial Value - Right Column */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 flex flex-col">
            <h3 className="text-lg font-semibold text-green-800 mb-2 h-14 flex items-center">
              Entrepreneurial Value
              {(() => {
                const synergyAnswers = data.entrepreneurialSynergy || defaultSynergyAnswers;
                const { factors } = calculateEntrepreneurialMultiplier(synergyAnswers);
                return factors.length > 0 && (
                  <span className="ml-2 inline-flex relative">
                    <button
                      className="text-green-600 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 rounded-full p-0.5"
                      aria-label="Entrepreneurial value breakdown"
                      onMouseEnter={(e) => {
                        const tooltip = e.currentTarget.nextElementSibling;
                        if (tooltip) {
                          (tooltip as HTMLElement).style.opacity = '1';
                          (tooltip as HTMLElement).style.visibility = 'visible';
                        }
                      }}
                      onMouseLeave={(e) => {
                        const tooltip = e.currentTarget.nextElementSibling;
                        if (tooltip) {
                          (tooltip as HTMLElement).style.opacity = '0';
                          (tooltip as HTMLElement).style.visibility = 'hidden';
                        }
                      }}
                    >
                      <HelpCircle className="w-4 h-4" />
                    </button>
                    <div
                      className="invisible opacity-0 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-800 text-white text-xs rounded-md p-2.5 w-64 z-10 shadow-lg transition-all duration-200"
                      style={{ pointerEvents: 'none' }}
                    >
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
                      <div className="font-semibold mb-1">Based on {factors.length} synergy factor{factors.length !== 1 ? 's' : ''}:</div>
                      {factors.map((factor, idx) => (
                        <div key={idx} className="text-[10px] leading-tight">
                          • {factor.name} ({factor.impact >= 0 ? '+' : ''}{(factor.impact * 100).toFixed(1)}%)
                        </div>
                      ))}
                    </div>
                  </span>
                );
              })()}
            </h3>
            <p className="text-3xl font-bold text-green-600 mb-2">
              ${(() => {
                const synergyAnswers = data.entrepreneurialSynergy || defaultSynergyAnswers;
                const { multiplier, uplift } = calculateEntrepreneurialMultiplier(synergyAnswers);

                // ALWAYS use totalValue (which includes fuel supply benefit) as the base
                // Entrepreneurial Value should be the HIGHEST value (base × multiplier)
                const entrepreneurialBase = totalValue || 0;
                const entrepreneurialValue = entrepreneurialBase * multiplier;

                const formattedValue = entrepreneurialValue >= 1000000
                  ? `${(entrepreneurialValue / 1000000).toFixed(2)}M`
                  : `${(entrepreneurialValue / 1000).toFixed(0)}K`;
                return formattedValue;
              })()}
            </p>
            <p className="text-sm text-green-700">
              {(() => {
                const synergyAnswers = data.entrepreneurialSynergy || defaultSynergyAnswers;
                const { uplift, factors } = calculateEntrepreneurialMultiplier(synergyAnswers);
                if (factors.length > 0) {
                  return `Your operational uplift (+${(uplift * 100).toFixed(1)}%)`;
                }
                return 'Based on average operational uplift (+15%)';
              })()}
            </p>
          </div>

        </div>

        {/* Dual Valuation Display - Hidden for soft launch */}
        {false && (
        <DualValuationWrapper
          apiResponse={apiResponse}
          data={data}
          totalValue={totalValue}
        />
        )}

        {/* Fuel Supply Benefit Display - Only show if benefit applies */}
        {fuelSupplyBenefit.qualifies && (
          <div className="mt-6 max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-300 shadow-sm p-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-purple-900 mb-1">
                    Fuel Supply Flexibility Premium Applied
                  </h3>
                  <p className="text-sm text-purple-800 leading-relaxed">
                    {fuelSupplyBenefit.reason}. Independent/unbranded stations and properties with short-term
                    fuel supply agreements command a market premium because buyers gain operational flexibility—they can
                    rebrand without penalty, negotiate better wholesale terms, and adapt quickly to market conditions.
                    This property qualifies for a <strong>{fuelSupplyBenefit.benefitPercentage}% flexibility premium</strong>,
                    adding <strong>+${(fuelSupplyBenefit.valuationAdjustment / 1000000).toFixed(2)}M</strong> to the
                    base valuation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confidence score hidden until dynamic logic is implemented */}
        {/* <div className="flex justify-center mt-2 text-sm">
          <div className="bg-blue-50 text-blue-800 px-3 py-1 rounded-full flex items-center">
            <Shield className="w-4 h-4 mr-1" />
            Confidence Score: {confidenceScore ? (confidenceScore * 10).toFixed(1) : '8.5'}/10
          </div>
        </div> */}
      </div>

      {/* Valuation Breakdown Section */}
      <div className="mb-8 border-b border-gray-200 pb-6">
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              Value Breakdown
              <span
                onClick={() => setActiveBreakdownTab('detailed')}
                className={`ml-2 text-sm px-2 py-1 rounded-md cursor-pointer ${
                  activeBreakdownTab === 'detailed'
                    ? 'text-blue-600 bg-blue-50 font-semibold'
                    : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                Detailed Analysis
              </span>
              <span
                onClick={() => setActiveBreakdownTab('input')}
                className={`ml-2 text-sm px-2 py-1 rounded-md cursor-pointer ${
                  activeBreakdownTab === 'input'
                    ? 'text-blue-600 bg-blue-50 font-semibold'
                    : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                Input Summary
              </span>
              {/* Performance Analysis button hidden per user request */}
              {/* <span
                className="ml-2 text-sm text-green-600 bg-green-50 px-2 py-1 rounded-md cursor-pointer"
                onClick={async () => {
                  try {
                    // Check if user is authenticated
                    if (!isAuthenticated || !user) {
                      console.error('User not authenticated');
                      return;
                    }

                    // Check if supabase is available
                    if (!supabase) {
                      console.error('Supabase not available');
                      return;
                    }

                    // Auto-save the valuation first
                    const saveResult = await saveValuationData(supabase, {
                      propertyId: uuidv4(), // Generate a UUID for the property
                      userId: user.id,
                      name: `Valuation - ${data.location || 'Property'} - ${new Date().toLocaleDateString()}`,
                      data: data
                    });

                    if (saveResult.success && saveResult.data?.id) {
                      // Navigate to performance analysis with the saved valuation ID
                      navigate(`/dashboard/performance-analysis/${saveResult.data.id}`);
                    } else {
                      console.error('Failed to save valuation:', saveResult.error);
                      // Fallback: generate a session ID and pass data via state
                      const sessionId = uuidv4();
                      navigate(`/dashboard/performance-analysis/${sessionId}`, { 
                        state: { valuationData: data, predictedValue, apiResponse } 
                      });
                    }
                  } catch (error) {
                    console.error('Error saving valuation:', error);
                    // Fallback: generate a session ID and pass data via state
                    const sessionId = uuidv4();
                    navigate(`/dashboard/performance-analysis/${sessionId}`, { 
                      state: { valuationData: data, predictedValue, apiResponse } 
                    });
                  }
                }}
              >
                Performance Analysis
              </span> */}
            </h3>

            {activeBreakdownTab === 'detailed' ? (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="space-y-4">
                  <div className="flex pb-2 border-b border-gray-200">
                    <span className="font-medium flex-1">Component</span>
                    <span className="font-medium w-48 text-right">Estimated Value</span>
                    <span className="font-medium w-40 text-right">Confidence Score</span>
                  </div>
                
                {/* Dynamically render all components from API data */}
                {valuationBreakdown.detailedComponents.map((component, index) => (
                  <div key={index} className="flex items-center py-1">
                    <span className="flex items-center flex-1 min-w-0">
                      <span className={`w-3 h-3 ${component.color} rounded-sm inline-block mr-2`}></span>
                      {component.name}
                      {component.hasTooltip && (
                        <span className="ml-2 inline-flex relative">
                          <button 
                            className="text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-full p-0.5"
                            aria-label={`Help about ${component.name}`}
                            onMouseEnter={(e) => {
                              const tooltip = e.currentTarget.nextElementSibling;
                              if (tooltip) {
                                (tooltip as HTMLElement).style.opacity = '1';
                                (tooltip as HTMLElement).style.visibility = 'visible';
                              }
                            }}
                            onMouseLeave={(e) => {
                              const tooltip = e.currentTarget.nextElementSibling;
                              if (tooltip) {
                                (tooltip as HTMLElement).style.opacity = '0';
                                (tooltip as HTMLElement).style.visibility = 'hidden';
                              }
                            }}
                          >
                            <HelpCircle className="w-4 h-4" />
                          </button>
                          <div 
                            className="invisible opacity-0 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-800 text-white text-xs rounded-md p-2.5 w-64 z-10 shadow-lg transition-all duration-200"
                            style={{ pointerEvents: 'none' }}
                          >
                            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
                            {component.tooltipText}
                          </div>
                        </span>
                      )}
                    </span>
                    <span className={`font-medium w-48 text-right ${component.value < 0 ? 'text-red-600' : ''}`}>
                      {component.value < 0 ? '-' : ''}${formatCurrency(Math.abs(component.value))}
                    </span>
                    <span className="text-blue-800 font-semibold w-40 text-right">{component.confidenceScore.toFixed(1)}</span>
                  </div>
                ))}
                
                  <div className="pt-3 mt-3 border-t border-gray-300 flex items-center font-bold">
                    <span className="flex-1">{leasedFee && leasedFee.leasedFeeValue > 0 ? 'Total Property Value' : 'Total Going-Concern Value'}</span>
                    <span className="text-blue-600 w-48 text-right">
                      ${valuationBreakdown.detailedComponents
                        .reduce((sum, c) => sum + Math.round(c.value / 10000) * 10000, 0)
                        .toLocaleString('en-US')}
                    </span>
                    <span className="text-blue-800 font-semibold w-40 text-right">8.5</span>
                  </div>
                </div>

                {/* Real vs Personal property allocation. Only renders when there's a
                    leased QSR component — for stations without one, the existing UI
                    is unchanged. */}
                {leasedFee && leasedFee.leasedFeeValue > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h4 className="text-lg font-semibold mb-1">Property Allocation</h4>
                    <p className="text-xs text-gray-500 mb-4">
                      How underwriters categorize the asset. Real property is illiquid
                      and tied to the location; personal property and intangibles can
                      transfer with the operator.
                    </p>

                    {/* Stacked horizontal bar */}
                    <div className="flex h-3 rounded-full overflow-hidden mb-4 bg-gray-100">
                      <div
                        className="bg-blue-600"
                        style={{ width: `${realPropertyPct}%` }}
                        title={`Real Property: ${realPropertyPct.toFixed(1)}%`}
                      />
                      <div
                        className="bg-emerald-500"
                        style={{ width: `${personalPropertyPct}%` }}
                        title={`Personal Property & Intangibles: ${personalPropertyPct.toFixed(1)}%`}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Real Property side */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-baseline justify-between mb-2">
                          <h5 className="font-semibold text-sm text-blue-900">
                            Real Property
                          </h5>
                          <span className="text-xs font-medium text-blue-700">
                            {realPropertyPct.toFixed(0)}%
                          </span>
                        </div>
                        <div className="text-2xl font-bold text-blue-700 mb-3">
                          ${formatCurrency(realPropertyTotal)}
                        </div>
                        <div className="space-y-1 text-sm">
                          {realPropertyItems.map((item) => (
                            <div key={item.name} className="flex justify-between">
                              <span className="text-blue-800">{item.name}</span>
                              <span className="font-medium text-blue-900">
                                ${formatCurrency(item.value)}
                              </span>
                            </div>
                          ))}
                        </div>
                        <p className="mt-3 text-xs text-blue-600 italic">
                          Land, buildings, permanent fixtures, and leased fee
                          real-estate income.
                        </p>
                      </div>

                      {/* Personal Property side */}
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                        <div className="flex items-baseline justify-between mb-2">
                          <h5 className="font-semibold text-sm text-emerald-900">
                            Personal Property &amp; Intangibles
                          </h5>
                          <span className="text-xs font-medium text-emerald-700">
                            {personalPropertyPct.toFixed(0)}%
                          </span>
                        </div>
                        <div className="text-2xl font-bold text-emerald-700 mb-3">
                          ${formatCurrency(personalPropertyTotal)}
                        </div>
                        <div className="space-y-1 text-sm">
                          {personalPropertyItems.map((item) => (
                            <div key={item.name} className="flex justify-between">
                              <span className="text-emerald-800">{item.name}</span>
                              <span className={`font-medium ${item.value < 0 ? 'text-red-600' : 'text-emerald-900'}`}>
                                {item.value < 0 ? '-' : ''}${formatCurrency(Math.abs(item.value))}
                              </span>
                            </div>
                          ))}
                        </div>
                        <p className="mt-3 text-xs text-emerald-600 italic">
                          FF&amp;E, business goodwill, and other transferable assets.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="text-lg font-semibold mb-4">Property Input Data</h4>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Property Information */}
                  <div>
                    <h5 className="font-semibold text-sm text-gray-700 mb-3 border-b pb-1">Property Details</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Location:</span>
                        <span className="font-medium text-right max-w-[60%]">{data.location || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Property Type:</span>
                        <span className="font-medium">{
                          data.propertyType === 'convenience-store' ? 'Convenience Store' :
                          data.propertyType === 'service-station' ? 'Service Station' :
                          data.propertyType === 'kiosk' ? 'Kiosk' :
                          data.propertyType === 'food-booth' ? 'Food Booth' :
                          data.propertyType || 'N/A'
                        }</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ownership:</span>
                        <span className="font-medium capitalize">{data.ownership || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Brand Affiliation:</span>
                        <span className="font-medium capitalize">{data.brandAffiliation || 'N/A'}</span>
                      </div>
                      {data.brandName && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Fuel Brand:</span>
                          <span className="font-medium">{data.brandName}</span>
                        </div>
                      )}
                      {data.storeBrand && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Store Brand:</span>
                          <span className="font-medium">{data.storeBrand}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Building Size:</span>
                        <span className="font-medium">{data.propertySize ? `${data.propertySize.toLocaleString()} sq ft` : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Lot Size:</span>
                        <span className="font-medium">{data.lotSize ? `${data.lotSize.toLocaleString()} sq ft` : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Canopy Size:</span>
                        <span className="font-medium">{data.canopySize ? `${data.canopySize.toLocaleString()} sq ft` : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Year Built:</span>
                        <span className="font-medium">{data.yearBuilt || 'N/A'}</span>
                      </div>
                      {data.yearRenovated && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Year Renovated:</span>
                          <span className="font-medium">{data.yearRenovated}</span>
                        </div>
                      )}
                      {data.trafficCount && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Traffic Count:</span>
                          <span className="font-medium">{data.trafficCount.toLocaleString()} ADT</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Operational Data */}
                  <div>
                    <h5 className="font-semibold text-sm text-gray-700 mb-3 border-b pb-1">Operational Metrics</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Number of Pumps:</span>
                        <span className="font-medium">{data.numberOfPumps || 'N/A'}</span>
                      </div>
                      {data.pumpAge && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Pump Age:</span>
                          <span className="font-medium">{data.pumpAge}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">UST Capacity:</span>
                        <span className="font-medium">{data.tankCapacity ? `${data.tankCapacity.toLocaleString()} gal` : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Annual Fuel Volume:</span>
                        <span className="font-medium">{data.annualGallons ? `${data.annualGallons.toLocaleString()} gal` : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Fuel Margin:</span>
                        <span className="font-medium">{data.fuelMargin ? `$${data.fuelMargin.toFixed(2)}/gal` : 'N/A'}</span>
                      </div>
                      {data.fuelTypes && data.fuelTypes.length > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Fuel Types:</span>
                          <span className="font-medium text-right max-w-[60%]">{data.fuelTypes.join(', ')}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Operating Hours:</span>
                        <span className="font-medium">{data.operatingHours || 'N/A'}</span>
                      </div>
                      {data.specificHours && data.operatingHours !== '24/7' && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Specific Hours:</span>
                          <span className="font-medium">{data.specificHours}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Fuel Supply Agreement:</span>
                        <span className="font-medium">
                          {data.noFuelAgreement ? 'None' : data.fuelSupplyYears ? `${data.fuelSupplyYears} years remaining` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Car Wash:</span>
                        <span className="font-medium">{data.hasCarWash ? 'Yes' : 'No'}</span>
                      </div>
                      {data.hasCarWash && data.carWashType && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Car Wash Type:</span>
                          <span className="font-medium capitalize">{data.carWashType}</span>
                        </div>
                      )}
                      {data.additionalServices && data.additionalServices.length > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Additional Services:</span>
                          <span className="font-medium text-right max-w-[60%]">{data.additionalServices.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Financial Data */}
                  <div>
                    <h5 className="font-semibold text-sm text-gray-700 mb-3 border-b pb-1">Financial Metrics</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Gross Revenue:</span>
                        <span className="font-medium">
                          {apiResponse?.result?.gross_revenue
                            ? `$${toMillions(apiResponse.result.gross_revenue)}M`
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">C-Store Revenue:</span>
                        <span className="font-medium">{data.storeRevenue ? `$${data.storeRevenue.toLocaleString()}` : 'N/A'}</span>
                      </div>
                      {data.storeMargin !== undefined && data.storeMargin > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">C-Store Margin:</span>
                          <span className="font-medium">{Math.round(data.storeMargin * 100)}%</span>
                        </div>
                      )}
                      {data.carWashRevenue !== undefined && data.carWashRevenue > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Car Wash Revenue:</span>
                          <span className="font-medium">${data.carWashRevenue.toLocaleString()}</span>
                        </div>
                      )}
                      {data.carWashMargin !== undefined && data.carWashMargin > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Car Wash Margin:</span>
                          <span className="font-medium">{Math.round(data.carWashMargin * 100)}%</span>
                        </div>
                      )}
                      {data.qsrRepairBayRevenue !== undefined && data.qsrRepairBayRevenue > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Misc. / Other Revenue:</span>
                          <span className="font-medium">${data.qsrRepairBayRevenue.toLocaleString()}</span>
                        </div>
                      )}
                      {data.qsrRepairBayMargin !== undefined && data.qsrRepairBayMargin > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Misc. / Other Margin:</span>
                          <span className="font-medium">{Math.round(data.qsrRepairBayMargin * 100)}%</span>
                        </div>
                      )}
                      {data.totalGrossProfit !== undefined && data.totalGrossProfit > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Gross Profit:</span>
                          <span className="font-medium">${data.totalGrossProfit.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Operating Expenses:</span>
                        <span className="font-medium">{data.operatingExpenses ? `$${data.operatingExpenses.toLocaleString()}` : 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* QSR / Leased Fee */}
                  {data.hasLeasedQSR && (
                    <div>
                      <h5 className="font-semibold text-sm text-gray-700 mb-3 border-b pb-1">Leased QSR</h5>
                      <div className="space-y-2 text-sm">
                        {data.qsrTenantName && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Tenant:</span>
                            <span className="font-medium">{data.qsrTenantName}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Drive-Thru:</span>
                          <span className="font-medium">{data.qsrHasDriveThru ? 'Yes' : 'No'}</span>
                        </div>
                        {data.qsrCurrentMonthlyRent !== undefined && data.qsrCurrentMonthlyRent > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Monthly Rent:</span>
                            <span className="font-medium">${data.qsrCurrentMonthlyRent.toLocaleString()}</span>
                          </div>
                        )}
                        {data.qsrExpenseType && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Lease Type:</span>
                            <span className="font-medium">{data.qsrExpenseType}</span>
                          </div>
                        )}
                        {data.qsrIsMonthToMonth ? (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Lease Term:</span>
                            <span className="font-medium">Month-to-Month</span>
                          </div>
                        ) : (
                          <>
                            {data.qsrLeaseStartYear && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Lease Start:</span>
                                <span className="font-medium">{data.qsrLeaseStartYear}</span>
                              </div>
                            )}
                            {data.qsrLeaseEndYear && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Lease End:</span>
                                <span className="font-medium">{data.qsrLeaseEndYear}</span>
                              </div>
                            )}
                            {data.qsrOptionsTotal !== undefined && data.qsrOptionsTotal > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Option Years:</span>
                                <span className="font-medium">{data.qsrOptionsTotal} years</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Leased Fee Asset Valuation (computed when QSR rent is on file) */}
                  {leasedFee && leasedFee.leasedFeeValue > 0 && (
                    <div className="md:col-span-2 bg-fc-orange-50 border border-fc-orange-200 rounded-lg p-4">
                      <h5 className="font-semibold text-sm text-gray-800 mb-3 border-b border-fc-orange-200 pb-1">
                        Leased Fee Asset Valuation
                      </h5>
                      <p className="text-xs text-gray-600 mb-3">
                        Valued separately from the going concern via income capitalization.
                        Net operating income divided by an applied cap rate driven by tenant
                        tier, lease term, and location.
                      </p>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Monthly Rent:</span>
                            <span className="font-medium">${leasedFee.monthlyRent.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Annual Rent:</span>
                            <span className="font-medium">${leasedFee.annualRent.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Vacancy &amp; Loss ({(leasedFee.vacancyAndLossRate * 100).toFixed(1)}%):
                            </span>
                            <span className="font-medium">−${Math.round(leasedFee.vacancyAndLossAmount).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between border-t border-fc-orange-200 pt-2">
                            <span className="text-gray-700 font-medium">NOI:</span>
                            <span className="font-semibold">${Math.round(leasedFee.noi).toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="text-gray-700 font-medium mb-1">Cap Rate Build-up</div>
                          {leasedFee.capRate.adjustments.map((adj, i) => (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-gray-600">{adj.reason}:</span>
                              <span className={`font-medium ${
                                adj.factor === 'base' ? 'text-gray-700'
                                : adj.basisPoints > 0 ? 'text-red-600'
                                : adj.basisPoints < 0 ? 'text-green-600'
                                : 'text-gray-500'
                              }`}>
                                {adj.factor === 'base'
                                  ? `${adj.basisPoints} bp`
                                  : `${adj.basisPoints > 0 ? '+' : ''}${adj.basisPoints} bp`}
                              </span>
                            </div>
                          ))}
                          <div className="flex justify-between border-t border-fc-orange-200 pt-2">
                            <span className="text-gray-700 font-medium">Applied Cap Rate:</span>
                            <span className="font-semibold">{(leasedFee.capRate.appliedRate * 100).toFixed(2)}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-fc-orange-300 flex justify-between items-baseline">
                        <span className="text-gray-800 font-semibold">Leased Fee Value:</span>
                        <span className="text-fc-orange-700 font-bold text-lg">
                          ${Math.round(leasedFee.leasedFeeValue).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-fc-orange-200 flex justify-between items-baseline">
                        <span className="text-gray-600 text-sm">Going Concern + Leased Fee:</span>
                        <span className="text-gray-900 font-semibold">
                          ${Math.round(grandTotalValue).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Asset Conditions */}
                  <div>
                    <h5 className="font-semibold text-sm text-gray-700 mb-3 border-b pb-1">Asset Conditions</h5>
                    <div className="space-y-2 text-sm">
                      {data.propertyCondition && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Building Condition:</span>
                          <span className="font-medium">{data.propertyCondition}</span>
                        </div>
                      )}
                      {data.assets?.Fueling_Canopy?.condition && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Canopy Condition:</span>
                          <span className="font-medium">{data.assets.Fueling_Canopy.condition}</span>
                        </div>
                      )}
                      {data.assets?.Site_Improvements?.condition && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Site Condition:</span>
                          <span className="font-medium">{data.assets.Site_Improvements.condition}</span>
                        </div>
                      )}
                      {data.hasCarWash && data.assets?.Car_Wash?.condition && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Car Wash Condition:</span>
                          <span className="font-medium">{data.assets.Car_Wash.condition}</span>
                        </div>
                      )}
                      {data.ustCondition && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">UST Condition:</span>
                          <span className="font-medium">{data.ustCondition}</span>
                        </div>
                      )}
                      {data.ustAge && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">UST Age:</span>
                          <span className="font-medium">{data.ustAge}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                label: 'Gross Revenue',
                value: apiResponse?.result?.gross_revenue 
                  ? `$${toMillions(apiResponse.result.gross_revenue)}M` 
                  : `$${(Number(data.monthlyGallons) * 12 * Number(data.fuelMargin) / 1000000).toFixed(2)}M`,
                icon: <TrendingUp className="w-6 h-6" />
              },
              {
                label: 'Gross Profit',
                value: (() => {
                  // Prioritize our comprehensive totalGrossProfit calculation over API's basic calculation
                  const comprehensive = data.totalGrossProfit;
                  const apiBasic = apiResponse?.result?.gross_profit;
                  const fallback = Number(data.storeRevenue);

                  const displayValue = comprehensive || apiBasic || fallback;

                  return displayValue >= 1000000
                    ? `$${(displayValue / 1000000).toFixed(2)}M`
                    : `$${(displayValue / 1000).toFixed(0)}K`;
                })(),
                icon: <Building2 className="w-6 h-6" />
              },
              {
                label: 'Annual Gallons',
                value: apiResponse?.result?.total_gallons
                  ? `${apiResponse.result.total_gallons.toLocaleString()}`
                  : `${(Number(data.monthlyGallons) * 12).toLocaleString()}`,
                icon: <Fuel className="w-6 h-6" />
              }
            ].map((metric) => (
              <div
                key={metric.label}
                className="bg-gray-50 p-4 rounded-lg"
              >
                <div className="flex items-center gap-2 text-blue-600 mb-2">
                  {metric.icon}
                  <span className="font-semibold">{metric.label}</span>
                </div>
                <p className="text-2xl font-bold">{metric.value}</p>
              </div>
            ))}
          </div>
        </div>

      {/* Valuation Tools & Analytics Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Valuation Tools & Analytics</h2>

        {/* Tax Depreciation Calculator */}
        <div className="mb-6">
          <TaxDepreciationDemo totalPropertyValue={totalValue} />
        </div>

        {/* Loan Calculator */}
        <div className="mb-6">
          {(() => {
            const noi = apiResponse?.result?.tam_estimation?.estimates?.projections?.year_1?.net_operating_income ||
                        apiResponse?.result?.tam_estimation?.estimates?.year_1_noi ||
                        (apiResponse?.result?.gross_profit ? apiResponse.result.gross_profit * 0.35 : undefined);

            console.log('[ValuationResult] Loan Calculator Data:', {
              totalPropertyValue: totalValue,
              goingConcernNOI: noi,
              businessValue: apiResponse?.result?.predicted_business_value,
              ffeValue: apiResponse?.result?.depreciated_assets?.FFE?.Depreciated_Cost,
              tam_path1: apiResponse?.result?.tam_estimation?.estimates?.projections?.year_1?.net_operating_income,
              tam_path2: apiResponse?.result?.tam_estimation?.estimates?.year_1_noi,
              gross_profit_fallback: apiResponse?.result?.gross_profit
            });

            return (
              <LoanCalculatorDemo
                totalPropertyValue={totalValue}
                goingConcernNOI={noi}
                businessValue={apiResponse?.result?.predicted_business_value || 0}
                ffeValue={apiResponse?.result?.depreciated_assets?.FFE?.Depreciated_Cost || 0}
              />
            );
          })()}
        </div>

        {/* Performance Benchmarks - Subject vs Cohort vs National.
            Hidden entirely when any of the three data branches is missing — a partial
            comparison is meaningless. Cohort and national benchmark data live in
            Supabase, not in the regressor response. */}
        {apiResponse?.result?.tam_estimation?.subject &&
         apiResponse?.result?.tam_estimation?.diagnostics?.comparables_analysis &&
         apiResponse?.result?.tam_estimation?.diagnostics?.benchmark && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">Performance Benchmarks</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Subject Property */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="text-lg font-semibold text-blue-700 mb-2">Subject Property</h4>
              {apiResponse?.result?.tam_estimation?.subject && (
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">Fuel Volume:</span> {apiResponse.result.tam_estimation.subject.annual_fuel_gallons?.toLocaleString() || 'N/A'} gal</p>
                  <p><span className="font-medium">C-Store Sales:</span> ${apiResponse.result.tam_estimation.subject.annual_cstore_sales?.toLocaleString() || 'N/A'}</p>
                  <p><span className="font-medium">C-Store Margin:</span> {((apiResponse.result.tam_estimation.subject.cstore_margin || 0) * 100).toFixed(1)}%</p>
                  <p><span className="font-medium">NOI:</span> ${apiResponse.result.tam_estimation.estimates?.projections?.year_1?.net_operating_income?.toLocaleString() || 'N/A'}</p>
                </div>
              )}
            </div>

            {/* Cohort/OpComps Average */}
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="text-lg font-semibold text-green-700 mb-2">Market Cohort (OpComps)</h4>
              {apiResponse?.result?.tam_estimation?.diagnostics?.comparables_analysis && (
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">Comp Count:</span> {apiResponse.result.tam_estimation.diagnostics.comparables_analysis.comp_count || 'N/A'}</p>
                  <p><span className="font-medium">Avg Fuel Volume:</span> {apiResponse.result.tam_estimation.diagnostics.comparables_analysis.avg_fuel_gallons?.toLocaleString() || 'N/A'} gal</p>
                  <p><span className="font-medium">Avg C-Store Sales:</span> ${apiResponse.result.tam_estimation.diagnostics.comparables_analysis.avg_cstore_sales?.toLocaleString() || 'N/A'}</p>
                  <p><span className="font-medium">Location:</span> {apiResponse.result.tam_estimation.diagnostics.comparables_analysis.geography_description || 'N/A'}</p>
                </div>
              )}
            </div>

            {/* National Benchmarks */}
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="text-lg font-semibold text-purple-700 mb-2">National Benchmarks</h4>
              {apiResponse?.result?.tam_estimation?.diagnostics?.benchmark && (
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">Fuel Volume:</span> {apiResponse.result.tam_estimation.diagnostics.benchmark.Annual_Fuel_Gallons_National?.toLocaleString() || 'N/A'} gal</p>
                  <p><span className="font-medium">C-Store Sales:</span> ${apiResponse.result.tam_estimation.diagnostics.benchmark.Annual_Cstore_Sales_National?.toLocaleString() || 'N/A'}</p>
                  <p><span className="font-medium">C-Store Margin:</span> {((apiResponse.result.tam_estimation.diagnostics.benchmark.Annual_Cstore_Margin_National || 0) * 100).toFixed(1)}%</p>
                  <p><span className="font-medium">NOI:</span> ${apiResponse.result.tam_estimation.diagnostics.benchmark.Annual_Net_Operating_Income_National?.toLocaleString() || 'N/A'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Projection Methodology Note */}
        <div className="mb-6 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-5">
          <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center">
            <Info className="w-4 h-4 mr-1.5" />
            About Our Projection Methodology
          </h4>
          <div className="text-xs text-amber-900 space-y-2 leading-relaxed">
            <p>
              <strong>Fuel Margin Blending:</strong> Our fuel profit margin projection uses a 70/30 blend of real-time
              dynamic pricing data (derived from nearby station retail prices, rack costs, freight, and taxes) with
              historical cohort survey data from comparable operations. This blending approach tempers short-term
              market volatility — such as supply disruptions or seasonal spikes — by anchoring projections to
              longer-term operating norms. The blend is further adjusted by data confidence: when fewer nearby
              stations are available for comparison, the model leans more heavily on the cohort anchor.
            </p>
            <p>
              <strong>Volume-Margin Elasticity:</strong> High-volume stations typically sacrifice per-gallon margin
              to maintain throughput and market share, while lower-volume stations can sustain higher margins.
              Our model applies a volume-based compression factor that accounts for this inverse relationship,
              with brand context considered.
            </p>
          </div>
        </div>

        {/* Coming Soon Section */}
        <div className="mb-4 mt-8">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            Coming Soon
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">In Development</span>
          </h3>
          <p className="text-sm text-gray-500 mt-1">These features are actively being built and will be available in future updates.</p>
        </div>

        {/* Market Insights Placeholder */}
        <div className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-3 text-indigo-800 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2" />
            Market Insights
          </h3>
          <p className="text-gray-700 text-sm mb-3">
            <strong>Coming soon:</strong> AI-powered market commentary and comprehensive location analysis for your property, including:
          </p>
          <ul className="text-gray-600 text-sm space-y-2 ml-4">
            <li>• <strong>Location Analysis:</strong> Road accessibility scoring, traffic patterns, visibility metrics, and proximity to major highways and thoroughfares</li>
            <li>• <strong>SWOT Assessment:</strong> Strengths, weaknesses, opportunities, and threats specific to your property's market position</li>
            <li>• <strong>Market Trends:</strong> Local and regional market dynamics, competitive positioning, and growth opportunities</li>
            <li>• <strong>Narrative Insights:</strong> AI-generated commentary synthesizing competitive position, value drivers, and strategic recommendations</li>
          </ul>
          <p className="text-gray-500 text-xs italic mt-3">
            This comprehensive analysis will provide actionable intelligence to help you make informed decisions about your gas station investment.
          </p>
        </div>

        {/* Re-branding Strategy Teaser */}
        <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-2 text-amber-800 flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Re-branding Strategy Analysis
          </h3>
          <p className="text-gray-600 text-sm italic">
            Coming soon: Explore alternative branding scenarios with major fuel brands (Shell, Chevron, ConocoPhillips, etc.). Includes contract term analysis, incentive money calculations, rehabilitation costs, and market share opportunity assessment.
          </p>
        </div>

        {/* Investment Returns Teaser */}
        <div className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-2 text-emerald-800 flex items-center">
            <Calculator className="w-5 h-5 mr-2" />
            Investment Returns & DCF Analysis
          </h3>
          <p className="text-gray-600 text-sm italic">
            Coming soon: Short-term discounted cash flow analysis, ROI calculator, sensitivity analysis, and hold period optimization. Analyze how interest rate changes, WACC adjustments, and operational improvements impact your investment returns.
          </p>
        </div>
      </div>

      {/* Competitor Analysis Section */}
      <div className="mb-8 pt-4">
        {/* Competitor Analysis */}
        <div className="mb-8">
          <ValuationCompetitorAnalysis
            subjectData={{
              location: data.location,
              stationObjectId: data.stationObjectId,
              propertySize: data.propertySize,
              canopySize: data.canopySize,
              lotSize: data.lotSize,
              brandAffiliation: data.brandAffiliation,
              brandName: data.brandName,
              yearBuilt: data.yearBuilt,
              numberOfPumps: data.numberOfPumps,
              hasCarWash: data.hasCarWash,
              carWashType: data.carWashType
            }}
            radiusMiles={1}
          />
        </div>

        {/* Location Analysis and SWOT - Hidden per user request, text moved to Market Insights teaser */}
        {/* Location Analysis and SWOT sections are now incorporated into the Market Insights teaser above */}

        {/* Validation Issues - Hidden for production */}
      </div>

      <div className="mt-8 border-t pt-6">
        <h3 className="text-xl font-semibold mb-4">Next Steps</h3>
        <p className="text-gray-600 mb-6">
          Ready to explore your options? Connect with trusted professionals to take the next step in your gas station journey.
        </p>

        {/* Auto-save status */}
        {saveStatus === 'success' && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <Check className="w-4 h-4" />
            Valuation automatically saved to your dashboard.
          </div>
        )}

        <div className="flex flex-wrap lg:flex-nowrap gap-4">
          <Button
            id="connect-broker-button"
            onClick={() => setBrokerModalOpen(true)}
            className="flex items-center gap-2 px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            <Users className="w-5 h-5" />
            <span>Connect with a Local Broker</span>
          </Button>

          <Button
            id="connect-lender-button"
            disabled={true}
            className="flex items-center gap-2 px-6 py-2 rounded-lg bg-purple-600 text-white cursor-not-allowed opacity-60"
          >
            <DollarSign className="w-5 h-5" />
            <span>Connect with a Lender <span className="text-purple-200 text-xs">(Coming Soon)</span></span>
          </Button>
        </div>
        
        {/* Save error message */}
        {saveStatus === 'error' && saveError && (
          <div className="mt-2 text-sm text-red-600">
            <p className="flex items-center">
              <AlertTriangle className="w-4 h-4 mr-1" />
              {!isAuthenticated 
                ? 'Please log in to save this valuation.' 
                : saveError}
            </p>
          </div>
        )}
      </div>

      <BrokerConnectionModal
        isOpen={brokerModalOpen}
        onClose={() => setBrokerModalOpen(false)}
        propertyAddress={data.location || ''}
        stationObjectId={data.stationObjectId || ''}
      />
    </div>
  );
}

/**
 * Wrapper component to handle async dual valuation calculation
 */
function DualValuationWrapper({ apiResponse, data, totalValue }: {
  apiResponse: any;
  data: any;
  totalValue: number;
}) {
  const [dualValuation, setDualValuation] = React.useState<DualValuationResult | null>(null);
  const [isCalculating, setIsCalculating] = React.useState(false);

  React.useEffect(() => {
    async function calculateDual() {
      const forwardProjection = apiResponse?.result?.tam_estimation?.diagnostics?.forward_margin_projection;

      if (!forwardProjection) {
        setDualValuation(null);
        return;
      }

      setIsCalculating(true);

      try {
        // Get required values from API response
        const landValue = apiResponse?.result?.predicted_land_value || 0;
        const totalDepreciatedAssets = apiResponse?.result?.total_depreciated_assets || 0;
        const basePropertyValue = landValue + totalDepreciatedAssets;

        const annualGallons = apiResponse?.result?.total_gallons ||
                             (data.monthly_fuel_sales ? data.monthly_fuel_sales * 12 / 3.5 : 0);

        const grossProfit = apiResponse?.result?.gross_profit || 0;
        const totalStationValue = totalValue;

        // Determine brand status
        const brandName = data.brandName || data.brand_affiliation || null;
        const isBranded = brandName &&
          !['independent', 'unbranded', 'generic'].some(term =>
            brandName.toLowerCase().includes(term)
          );

        // Extract TAM cost components (freight, taxes, etc.)
        // Path: tam_estimation.diagnostics.margin_calculation.dynamic_margin.components
        const dynamicMargin = apiResponse?.result?.tam_estimation?.diagnostics?.margin_calculation?.dynamic_margin;
        const tamComponents = {
          freight_cpg: dynamicMargin?.components?.freight_cpg || 0,
          total_taxes_cpg: dynamicMargin?.components?.total_taxes_cpg || 0,
          local_sales_tax_cpg: dynamicMargin?.components?.local_sales_tax_cpg || 0,
          brand_adjustment_cpg: dynamicMargin?.components?.brand_adjustment_cpg || 0
        };

        console.log('[DualValuationWrapper] Calling calculateDualValuation with:', {
          basePropertyValue,
          annualGallons,
          totalStationValue,
          grossProfit,
          isBranded,
          tamComponents
        });

        // Calculate dual valuation (async)
        const result = await calculateDualValuation(
          basePropertyValue,
          annualGallons,
          forwardProjection,
          totalStationValue,
          grossProfit,
          isBranded,
          tamComponents
        );

        setDualValuation(result);
      } catch (error) {
        console.error('[DualValuationWrapper] Error calculating dual valuation:', error);
        setDualValuation(null);
      } finally {
        setIsCalculating(false);
      }
    }

    calculateDual();
  }, [apiResponse, data, totalValue]);

  if (!dualValuation) {
    return null;
  }

  if (isCalculating) {
    return (
      <div className="mt-6 max-w-5xl mx-auto">
        <div className="bg-gray-50 rounded-xl p-6 text-center">
          <p className="text-gray-600">Calculating dual valuation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 max-w-5xl mx-auto">
      <DualValuationDisplay dualValuation={dualValuation} />
    </div>
  );
}